import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { usePayroll } from '@/hooks/trackingTime';
import { sendPayrollEmail, sendPayrollTestEmail } from '@/api/payroll';
import { format, formatDistanceToNow } from 'date-fns';
import { getWorkingDaysInMonth } from '@/utils/time';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import toastifyInstance from '@/utils/toastify';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useQuery } from '@tanstack/react-query';
import { getMonitoringProgressTrackingTimeByAdmin } from '@/api/trackingTime';
import { getActivityStatistics } from '@/api/statistical/statistical';
import { batchRequests, getSuccessfulResults } from '@/utils/batchApi';

const PayrollPage: React.FC = () => {
  // Default to previous month when opening the page
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  });
  const { data, isLoading, month, generate, update, sendEmails, sendTestEmails } = usePayroll(selectedMonth);
  const [requiredDays, setRequiredDays] = useState<number>(() =>
    getWorkingDaysInMonth(
      new Date().getFullYear(),
      new Date().getMonth(), // previous month index (1-based handled below in effect)
    )
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [testEmail, setTestEmail] = useState<string>('nvphvn@gmail.com');
  const [sendingIds, setSendingIds] = useState<Map<string, 'send' | 'test'>>(new Map());

  const monthStr = useMemo(
    () => format(selectedMonth, 'yyyy-MM'),
    [selectedMonth]
  );

  // Fetch statistics data for each payroll entry
  const userIds = useMemo(() => {
    return data.map((entry) => entry.uid);
  }, [data]);

  // Fetch total working time for each user
  const { data: trackingTimeData } = useQuery({
    queryKey: ['payroll-tracking-time', monthStr, userIds.length],
    queryFn: async () => {
      if (userIds.length === 0) return [];
      const results = await batchRequests(
        userIds,
        (uid) =>
          getMonitoringProgressTrackingTimeByAdmin({
            uid,
            date: selectedMonth,
            queryByMonth: 'true',
          }),
        {
          chunkSize: 8,
          delayBetweenChunks: 200,
          maxConcurrency: 3,
        }
      );
      return getSuccessfulResults(results);
    },
    enabled: userIds.length > 0 && !isLoading,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch activity statistics (WFH, check-ins, etc.) for each user
  const { data: activityData } = useQuery({
    queryKey: ['payroll-activity', monthStr, userIds.length],
    queryFn: async () => {
      if (userIds.length === 0) return [];
      const results = await batchRequests(
        userIds,
        (uid) =>
          getActivityStatistics({
            uid,
            date: monthStr,
          }),
        {
          chunkSize: 8,
          delayBetweenChunks: 200,
          maxConcurrency: 3,
        }
      );
      return getSuccessfulResults(results);
    },
    enabled: userIds.length > 0 && !isLoading,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Create a map of statistics data by user ID
  // Match by user._id from response instead of array index to handle failed requests
  const statisticsMap = useMemo(() => {
    const map = new Map<string, { totalTime: number; wfh: number }>();
    if (trackingTimeData && activityData) {
      // Match tracking time data by user._id
      trackingTimeData.forEach((trackingTime) => {
        if (trackingTime?.user?._id) {
          const uid = trackingTime.user._id;
          const activity = activityData.find((a) => a?.user?._id === uid);
          if (activity) {
            map.set(uid, {
              totalTime: trackingTime.totalTime || 0,
              wfh: activity.wfh?.length || 0,
            });
          }
        }
      });
    }
    return map;
  }, [trackingTimeData, activityData]);

  // Format time function - show only hours
  const formatTime = useCallback((minutes: number) => {
    const decimalHours = minutes / 60;
    return `${decimalHours.toFixed(2)} hours`;
  }, []);

  const monthValue = useMemo(
    () => format(selectedMonth, 'yyyy-MM'),
    [selectedMonth]
  );

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [year, m] = e.target.value.split('-').map(Number);
    if (year && m) {
      setSelectedMonth(new Date(year, m - 1, 1));
    }
  };

  useEffect(() => {
    const year = selectedMonth.getFullYear();
    const monthIndex = selectedMonth.getMonth() + 1;
    setRequiredDays(getWorkingDaysInMonth(year, monthIndex));
  }, [selectedMonth]);

  const handleNumberChange = (id: string, field: 'bonus' | 'advance' | 'penalty' | 'yearEndBonus' | 'fixedSalary', value: string) => {
    const parsed = Number(value.replace(/,/g, ''));
    if (Number.isNaN(parsed)) return;
    update(id, { [field]: parsed } as any);
  };

  const handleSendEmail = async (id: string) => {
    try {
      setSendingIds(prev => new Map(prev).set(id, 'send'));
      await sendPayrollEmail(id);
      toastifyInstance.success('Email sent successfully');
    } catch (error: any) {
      toastifyInstance.error(error?.response?.data?.message || 'Failed to send email');
    } finally {
      setSendingIds(prev => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleSendTestEmail = async (id: string) => {
    try {
      setSendingIds(prev => new Map(prev).set(id, 'test'));
      await sendPayrollTestEmail(id, testEmail);
      toastifyInstance.success(`Test email sent successfully to ${testEmail}`);
    } catch (error: any) {
      toastifyInstance.error(error?.response?.data?.message || 'Failed to send test email');
    } finally {
      setSendingIds(prev => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <CardTitle className="text-xl sm:text-2xl">Payroll</CardTitle>
          <p className="text-sm text-muted-foreground">Month: {month}</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="month"
            value={monthValue}
            onChange={handleMonthChange}
            className="w-[160px]"
          />
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Input
                  type="number"
                  className="w-[100px]"
                  value={requiredDays}
                  min={0}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (!Number.isNaN(value) && value >= 0) {
                      setRequiredDays(value);
                    }
                  }}
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">
                  Working days for {month}. Default is calculated from weekdays in that month, but you can adjust it if needed.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const abortController = new AbortController();
              
              try {
                setIsGenerating(true);
                await generate(requiredDays, abortController.signal);
                toastifyInstance.success(`Payroll generated successfully for ${month}`);
              } catch (error: any) {
                // Don't show error if request was intentionally canceled
                if (error?.name === 'AbortError' || error?.message === 'Request canceled') {
                  return;
                }
                
                // Better error handling with more details
                const errorMessage = error?.response?.data?.message 
                  || error?.message 
                  || 'Failed to generate payroll. This may take a few minutes for large datasets. Please try again.';
                toastifyInstance.error(errorMessage);
                
                // If it's a timeout error, provide helpful message
                if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
                  toastifyInstance.error('Request timed out. The payroll generation may still be processing on the server. Please wait a moment and refresh the page to check if it completed.');
                }
              } finally {
                setIsGenerating(false);
              }
            }}
            disabled={isLoading || isGenerating}
          >
            {isGenerating ? 'Generating… (this may take several minutes)' : 'Generate'}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="default"
                size="sm"
                disabled={isLoading || data.length === 0}
              >
                Send emails
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="sm:max-w-lg bg-white">
              <AlertDialogHeader>
                <AlertDialogTitle>Send salary emails?</AlertDialogTitle>
                <AlertDialogDescription>
                  Salary details for <span className="font-semibold">{month}</span> will be sent
                  to all employees who have payroll entries and have not been emailed yet.
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    try {
                      await sendEmails();
                      toastifyInstance.success(`Salary emails sent successfully for ${month}`);
                    } catch (error: any) {
                      toastifyInstance.error(error?.response?.data?.message || 'Failed to send emails');
                    }
                  }}
                >
                  Send
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={isLoading || data.length === 0}
              >
                Send test emails
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="sm:max-w-lg bg-white">
              <AlertDialogHeader>
                <AlertDialogTitle>Send test salary emails?</AlertDialogTitle>
                <AlertDialogDescription>
                  All salary emails for <span className="font-semibold">{month}</span> will be
                  sent to the address below instead of each employee&apos;s email.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="mt-4">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    try {
                      await sendTestEmails(testEmail);
                      toastifyInstance.success(`Test emails sent successfully to ${testEmail} for ${month}`);
                    } catch (error: any) {
                      toastifyInstance.error(error?.response?.data?.message || 'Failed to send test emails');
                    }
                  }}
                >
                  Send
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-muted-foreground">Loading payroll...</p>}
        {!isLoading && data.length === 0 && (
          <p className="text-sm text-muted-foreground">No payroll data for this month. Click Generate to create it.</p>
        )}
        {!isLoading && data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs sm:text-sm border">
              <thead>
                <tr className="bg-muted">
                  <th className="px-2 py-1 border">Employee</th>
                  <th className="px-2 py-1 border">Total Working Time</th>
                  <th className="px-2 py-1 border">Number of Late Days</th>
                  <th className="px-2 py-1 border">Number of Leave Days</th>
                  <th className="px-2 py-1 border">Fixed salary</th>
                  <th className="px-2 py-1 border">Checkin days</th>
                  <th className="px-2 py-1 border">Rounded days</th>
                  <th className="px-2 py-1 border">Factor (Rounded days / Required days)</th>
                  <th className="px-2 py-1 border">Regular Pay</th>
                  <th className="px-2 py-1 border">Bonus</th>
                  <th className="px-2 py-1 border">Total</th>
                  <th className="px-2 py-1 border">Advance</th>
                  <th className="px-2 py-1 border">Receive</th>
                  <th className="px-2 py-1 border">Opens</th>
                  <th className="px-2 py-1 border">Last opened</th>
                  <th className="px-2 py-1 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => {
                  // Use stored totalWorkingTimeMinutes from payroll record, fallback to fetched data if not available
                  const totalTime = row.totalWorkingTimeMinutes || statisticsMap.get(row.uid)?.totalTime || 0;
                  
                  // Calculate values for payroll columns
                  const loggedDays = row.totalCheckIns || 0; // Checkin days = Number of Check-ins
                  const requiredDays = row.requiredWorkingDays || 1; // Avoid division by zero
                  
                  // Calculate Rounded days: CEIL(Total Working Time in hours / (required working days * 7.5))
                  const totalHours = totalTime / 60; // Convert minutes to hours
                  const hoursPerDay = 7.5;
                  const totalRequiredHours = requiredDays * hoursPerDay;
                  // Formula: CEIL(totalHours / (requiredDays * 7.5))
                  const roundedDays = totalRequiredHours > 0 ? Math.ceil(totalHours / totalRequiredHours) : 0;
                  
                  // Factor = Rounded days / Required days
                  const factor = requiredDays > 0 ? roundedDays / requiredDays : 0;
                  const regularPay = Math.round((row.fixedSalary || 0) * factor);
                  const total = regularPay + (row.bonus || 0);
                  
                  return (
                  <tr key={row._id} className="border-t">
                    <td className="px-2 py-1 border whitespace-nowrap">{row.name}</td>
                      <td className="px-2 py-1 border text-center">
                        {formatTime(totalTime)}
                      </td>
                    <td className="px-2 py-1 border text-center">{row.lateDays}</td>
                      <td className="px-2 py-1 border text-center">{row.totalLeave}</td>
                    <td className="px-2 py-1 border">
                      <Input
                        type="number"
                        defaultValue={row.fixedSalary}
                        onBlur={(e) => handleNumberChange(row._id, 'fixedSalary', e.target.value)}
                        className="h-8"
                      />
                    </td>
                      <td className="px-2 py-1 border text-center">{loggedDays}</td>
                      <td className="px-2 py-1 border text-center">{roundedDays}</td>
                      <td className="px-2 py-1 border text-center">
                        {roundedDays} / {requiredDays} = {factor.toFixed(7)}
                      </td>
                      <td className="px-2 py-1 border text-right">{regularPay.toLocaleString()}</td>
                    <td className="px-2 py-1 border">
                      <Input
                        type="number"
                        defaultValue={row.bonus}
                        onBlur={(e) => handleNumberChange(row._id, 'bonus', e.target.value)}
                        className="h-8"
                      />
                    </td>
                       <td className="px-2 py-1 border text-right">{total.toLocaleString()}</td>
                    <td className="px-2 py-1 border">
                      <Input
                        type="number"
                        defaultValue={row.advance}
                        onBlur={(e) => handleNumberChange(row._id, 'advance', e.target.value)}
                        className="h-8"
                      />
                    </td>
                    <td className="px-2 py-1 border text-right">
                      {row.receive.toLocaleString()}
                    </td>
                    <td className="px-2 py-1 border text-center">
                      {row.openCount || 0}
                    </td>
                    <td className="px-2 py-1 border text-xs">
                      {row.lastOpenedAt
                        ? formatDistanceToNow(new Date(row.lastOpenedAt), { addSuffix: true })
                        : 'Never'}
                    </td>
                    <td className="px-2 py-1 border">
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendEmail(row._id)}
                          disabled={sendingIds.has(row._id) || !row.email}
                          className="h-7 text-xs"
                        >
                          {sendingIds.get(row._id) === 'send' ? '...' : 'Send'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendTestEmail(row._id)}
                          disabled={sendingIds.has(row._id)}
                          className="h-7 text-xs"
                        >
                          {sendingIds.get(row._id) === 'test' ? '...' : 'Test'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PayrollPage;


